/**
 * OAuth 인증 관련 유틸리티 함수들
 *
 * 이 파일은 소셜 로그인(카카오, 네이버, 구글 등)에 필요한
 * OAuth 인증 관련 유틸리티 함수들을 제공합니다.
 */

import { API_ENDPOINTS } from "@/constants/api";
import { setAccessToken } from "@/utils/tokenUtils";
import api from "@/utils/api";

/**
 * OAuth 제공자 타입 정의
 */
export type OAuthProvider = "KAKAO" | "NAVER" | "GOOGLE";

/**
 * OAuth 인증 응답 인터페이스
 */
export interface OAuthResponse {
    success: boolean;
    message: string;
    data: {
        accessToken: string;
        refreshToken: string;
        temporaryToken: string;
        isRegistered: boolean;
    };
    errorCode?: string;
}

/**
 * OAuth 인증 시작 함수
 *
 * @param provider - OAuth 제공자 (KAKAO, NAVER, GOOGLE)
 * @returns void
 */
export const startOAuthLogin = (provider: OAuthProvider): void => {
    let clientId = "";
    let redirectUri = "";
    let authUrl = "";

    // 제공자별 설정
    switch (provider) {
        case "KAKAO":
            clientId = process.env.REACT_APP_KAKAO_CLIENT_ID || "";
            redirectUri = process.env.REACT_APP_KAKAO_REDIRECT_URI || "";
            authUrl = `https://kauth.kakao.com/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code`;
            break;
        case "NAVER":
            // 네이버 로그인 구현 시 추가
            clientId = process.env.REACT_APP_NAVER_CLIENT_ID || "";
            redirectUri = process.env.REACT_APP_NAVER_REDIRECT_URI || "";
            authUrl = `https://nid.naver.com/oauth2.0/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&state=STATE_STRING`;
            break;
        case "GOOGLE":
            // 구글 로그인 구현 시 추가
            clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
            redirectUri = process.env.REACT_APP_GOOGLE_REDIRECT_URI || "";
            authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=email profile`;
            break;
        default:
            console.error("지원하지 않는 OAuth 제공자입니다.");
            return;
    }

    // 필수 정보가 없는 경우 에러 처리
    if (!clientId || !redirectUri) {
        console.error(`${provider} 로그인에 필요한 환경 변수가 설정되지 않았습니다.`);
        return;
    }

    // OAuth 인증 페이지로 리다이렉트
    window.location.href = authUrl;
};

/**
 * OAuth 콜백 처리 함수
 *
 * @param provider - OAuth 제공자 (KAKAO, NAVER, GOOGLE)
 * @param code - 인증 코드
 * @returns Promise<OAuthResponse> - OAuth 인증 응답
 * @throws {Error} 서버 응답의 오류 메시지를 포함한 에러
 */
export const handleOAuthCallback = async (provider: OAuthProvider, code: string): Promise<OAuthResponse> => {
    try {
        // 백엔드 API에 인증 코드 전송
        const response = await api.post<OAuthResponse>(API_ENDPOINTS.SNS_LOGIN, {
            snsProvider: provider,
            accessCode: code
        });

        const { data } = response.data;

        // 가입된 사용자인 경우
        if (data.isRegistered) {
            // 액세스 토큰 저장
            setAccessToken(data.accessToken);
        } else {
            // 미가입 사용자인 경우 임시 토큰 저장
            localStorage.setItem("temporaryToken", data.temporaryToken);
        }

        return response.data;
    } catch (error: any) {
        console.error(`${provider} 로그인 처리 중 오류가 발생했습니다:`, error);

        // axios 에러 응답에서 서버 에러 메시지 추출
        if (error.response && error.response.data) {
            const errorData = error.response.data;
            // 에러 정보가 있는 경우 해당 정보를 포함한 에러 객체 생성
            const errorMessage = errorData.message || "알 수 없는 오류가 발생했습니다.";
            const errorCode = errorData.errorCode || "UNKNOWN_ERROR";

            // 에러 정보를 담은 객체 생성
            const enhancedError = new Error(errorMessage);
            // 추가 정보 저장
            enhancedError.cause = {
                errorCode,
                originalError: error,
                serverResponse: errorData
            };

            throw enhancedError;
        }

        // 기본 에러 전달
        throw error;
    }
};
